import React from "react";
import { NotificationsModel } from "app/models/NotificationModel";
import { useTranslation } from "react-i18next";

interface NotificationProps {
  notification: NotificationsModel;
}

type PostType = "image" | "video" | "audio" | "datafile" | "note" | "gallery";

const getPostType = (notification: NotificationsModel): PostType | null => {
  return notification.meta &&
    notification.meta.post &&
    notification.meta.post.type
    ? notification.meta.post.type
    : null;
};

const formatMessageWithHeadline = (
  initiatorName: string,
  actionText: string,
  headline?: string,
): JSX.Element => {
  if (headline) {
    return (
      <span>
        <b>{initiatorName}</b> {actionText}: &quot;{headline}&quot;
      </span>
    );
  }
  return (
    <span>
      <b>{initiatorName}</b> {actionText}.
    </span>
  );
};

const NotificationMessage = ({ notification }: NotificationProps) => {
  const { t } = useTranslation();
  const postType = getPostType(notification);
  const { initiator } = notification;
  const headline = notification.meta?.post?.headline;

  switch (postType) {
    case "image":
      return formatMessageWithHeadline(
        initiator.nameShort,
        t("posted an image"),
        headline,
      );

    case "video":
      return formatMessageWithHeadline(
        initiator.nameShort,
        t("posted a video"),
        headline,
      );

    case "audio":
      return formatMessageWithHeadline(
        initiator.nameShort,
        t("posted audio"),
        headline,
      );

    case "datafile":
      return formatMessageWithHeadline(
        initiator.nameShort,
        t("posted a document"),
        headline,
      );

    case "gallery":
      return formatMessageWithHeadline(
        initiator.nameShort,
        t("posted a gallery"),
        headline,
      );

    case "note":
      switch (notification.event) {
        case "post.created":
          return (
            <span>
              <b>{initiator.nameShort}</b> {t("created a post")}
              {headline ? `: "${headline}"` : "."}
            </span>
          );

        case "reply.created":
          return (
            <span>
              <b>{initiator.nameShort}</b> {t("replied to a post")}
              {headline ? `: "${headline}"` : "."}
            </span>
          );

        default:
          return null;
      }

    default:
      return null;
  }
};

const LikeFormatted = ({ notification }: NotificationProps) => {
  const { t } = useTranslation();
  const postType = getPostType(notification);
  const initiatorName = notification.initiator.nameShort;

  switch (postType) {
    case "image":
      return (
        <span>
          <b>{initiatorName}</b> {t("liked your image")}.
        </span>
      );

    case "video":
      return (
        <span>
          <b>{initiatorName}</b> {t("liked your video")}.
        </span>
      );

    case "audio":
      return (
        <span>
          <b>{initiatorName}</b> {t("liked your audio")}.
        </span>
      );

    case "datafile":
      return (
        <span>
          <b>{initiatorName}</b> {t("liked your document")}.
        </span>
      );

    case "note":
      return (
        <span>
          <b>{initiatorName}</b> {t("liked your post")}.
        </span>
      );

    default:
      return null;
  }
};

export const getNotificationLabel = (notification: NotificationsModel) => {
  const { t } = useTranslation();
  const { event, initiator, message, meta } = notification;

  switch (event) {
    case "post.created":
    case "reply.created":
      return <NotificationMessage notification={notification} />;

    case "like.created":
      return <LikeFormatted notification={notification} />;

    case "read_receipt.sent":
      return (
        <span>
          <b>{message}</b>
        </span>
      );

    case "invitation.declined":
      return (
        <span>
          <b>{initiator.nameShort}</b> {t("rejected your invitation")}.
        </span>
      );

    case "invitation.accepted":
      return (
        <span>
          <b>{initiator.nameShort}</b> {t("joined space")}
        </span>
      );

    case "team_invitation.accepted":
      return (
        <span>
          <b>{initiator.nameShort}</b>{" "}
          {t("joined the {{group}} Group.", {
            group: meta?.team?.teamName,
          })}
        </span>
      );

    case "team_group_membership.created":
      return (
        <span>
          <b>{initiator.nameShort}</b>, {t("channel")}: {meta.teamGroup.name}
        </span>
      );

    case "team.ownership_transferred":
      if (meta.teamGroup && meta.teamGroup.name) {
        return (
          <span>
            <b>{initiator.nameShort}</b> {t("transferred ownership of")}{" "}
            {meta.teamGroup.name} {t("to you")}.
          </span>
        );
      }
      return (
        <span>
          <b>{message}</b>
        </span>
      );

    case "space.ownership_transferred":
    case "space_invitation.created_auto_accepted":
    case "team_invitation.created_auto_accepted":
      return (
        <span>
          <b>{message}</b>
        </span>
      );

    default:
      return null;
  }
};

export default NotificationMessage;
